<template>
  <div>
    <CModal
      class="twofa-modal"
      add-content-classes="twofa-shell"
      :show="is2FA"
      centered
    >
      <template #header-wrapper>
        <div class="mb-4"></div>
      </template>

      <template #body-wrapper>
        <CRow class="justify-content-center">
          <CCol col="10" lg="9">
            <CForm class="twofa-card" @submit.prevent="onSubmitCode">
              <div class="text-center mb-4">
                <img src="@/assets/mfu_logo.png" height="92" alt="MFU logo" class="twofa-logo" />
              </div>

              <div class="text-center twofa-copy">
                <p class="twofa-eyebrow">Two-Factor Authentication</p>
                <p class="h4 font-weight-bold mb-2 twofa-title">Verify Your Account</p>
                <p class="twofa-description">Enter the 6-digit verification code to finish signing in.</p>
                <p v-if="userEmail" class="twofa-email"><strong>Code sent to:</strong> {{ userEmail }}</p>
                <p v-if="devCode" class="twofa-dev-code">Local dev code: <strong>{{ devCode }}</strong></p>
              </div>

              <div class="otp-wrapper mt-4">
                <div
                  v-for="(digit, index) in otpInputs"
                  :key="index"
                  class="otp-input"
                >
                  <input
                    v-model="otpInputs[index]"
                    type="text"
                    maxlength="1"
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    @input="onInput(index)"
                    @keydown.backspace="onBackspace(index)"
                    ref="otpRefs"
                  />
                </div>
              </div>

              <CRow class="mt-4 text-center">
                <CCol>
                  <p class="twofa-help">It may take up to a minute to receive your code.</p>
                  <p class="twofa-help mb-0">
                    Haven't received it?
                    <button
                      type="button"
                      class="twofa-link"
                      :disabled="requestingCode"
                      @click="onRequestTwoFa()"
                    >
                      {{ requestingCode ? 'Sending...' : 'Resend a new code' }}
                    </button>
                  </p>
                </CCol>
              </CRow>
            </CForm>
          </CCol>
        </CRow>
      </template>

      <template #footer-wrapper>
        <div class="mb-4"></div>
      </template>
    </CModal>
    <TrustDeviceDialog :show="showTrustDeviceDialog" @close="onCloseTrustDialog"/>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import TrustDeviceDialog from '@/projects/components/dialog/TrustDeviceDialog.vue'

export default {
  name: 'TwoFA',
  components: {
    TrustDeviceDialog
  },
  data() {
    return {
      otpLength: 6,
      otpInputs: [],
      lastSubmittedCode: '',
      submittingCode: false,
      requestingCode: false,
      showTrustDeviceDialog: false
    }
  },
  mounted() {
    this.otpInputs = Array(this.otpLength).fill('')
  },
  methods: {
    async onRequestTwoFa() {
      if (this.requestingCode) {
        return
      }
      this.requestingCode = true
      try {
        await this.$store.dispatch("auth/twofa", {})
      } catch (err) {
        this.$store.commit('dialog/showError', {
          title: "Authentication Error",
          message: "Unable to resend verification code. Please try again.",
          code: "AUTH_2FA_REQUEST_FAILED",
          number: "1",
          status: true
        })
      } finally {
        this.requestingCode = false
      }
    },
    onInput(index) {
      const rawInput = this.otpInputs[index]
      const input = String(rawInput || '').slice(-1)
      if (!/^[a-zA-Z0-9]$/.test(input)) {
        this.$set(this.otpInputs, index, '')
        return
      }
      this.$set(this.otpInputs, index, input)
      if (index < this.otpLength - 1) {
        this.$refs.otpRefs[index + 1].focus()
      }
    },
    onBackspace(index) {
      if (this.otpInputs[index] === '' && index > 0) {
        this.$refs.otpRefs[index - 1].focus()
      }
    },
    onSubmitCode() {
      const code = this.otpInputs.join('')
      if (code.length === this.otpLength) {
        this.dispatchOtpCode(code)
      }
    },
    async dispatchOtpCode(code) {
      if (this.submittingCode || code.length !== this.otpLength || code === this.lastSubmittedCode) {
        return
      }
      this.submittingCode = true
      try {
        await this.$store.dispatch("auth/twofaSend", { code })
        this.lastSubmittedCode = code
        this.showTrustDeviceDialog = true
      } catch (err) {
        this.$store.commit('dialog/showError', {
          title: "Authentication Error",
          message: "Invalid or expired verification code. Please request a new code and try again.",
          code: "AUTH_2FA_VERIFY_FAILED",
          number: "1",
          status: true
        })
      } finally {
        this.submittingCode = false
      }
    },
    resetOtpInputs() {
      this.otpInputs = Array(this.otpLength).fill('')
      this.lastSubmittedCode = ''
      this.submittingCode = false
      this.requestingCode = false
      this.showTrustDeviceDialog = false
    },
    onCloseTrustDialog() {
      this.showTrustDeviceDialog = false
      this.resetOtpInputs()
      const authState = this.$store.getters['auth/authenticated'] || {}
      if (!authState.isAuthen) {
        this.$store.dispatch('auth/completeSignInFlow', { trustDevice: false })
      }
    }
  },

  computed: {
    ...mapGetters({
      is2FA: 'auth/is2FA',
      profile: "auth/profile",
      twoFAChallenge: 'auth/twoFAChallenge'
    }),

    userEmail() {
      try {
        if (this.profile && this.profile.email) {
          return String(this.profile.email)
        }
        if (this.profile && Array.isArray(this.profile.authen)) {
          const authenEmail = this.profile.authen.find(item => item && item.email)
          if (authenEmail && authenEmail.email) {
            return String(authenEmail.email)
          }
        }
        if (this.twoFAChallenge && this.twoFAChallenge.targetEmail) {
          return String(this.twoFAChallenge.targetEmail)
        }
        return ''
      } catch (err) {
        return ''
      }
    },
    devCode() {
      if (this.twoFAChallenge && this.twoFAChallenge.devCode) {
        return String(this.twoFAChallenge.devCode)
      }
      return ''
    }
  },

  watch: {
    otpInputs(value) {
      const isCompleted = value.every(v => v !== "" && v != null)
      if (!isCompleted) {
        return
      }
      this.dispatchOtpCode(value.join(""))
    },
    is2FA(value) {
      if (value) {
        this.resetOtpInputs()
        this.$nextTick(() => {
          if (this.$refs.otpRefs && this.$refs.otpRefs[0]) {
            this.$refs.otpRefs[0].focus()
          }
        })
      }
    }
  }
}
</script>

<style scoped>
.twofa-modal ::v-deep .modal-dialog {
  max-width: 560px;
}

.twofa-modal ::v-deep .modal-content {
  border: 0;
  border-radius: 28px;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(255, 246, 246, 0.98) 0%, rgba(255, 252, 247, 0.99) 100%);
  box-shadow: 0 28px 60px rgba(45, 13, 19, 0.34);
}

.twofa-modal ::v-deep .modal-body {
  color: #4b2430;
  padding-bottom: 28px;
}

.twofa-shell {
  background: transparent !important;
}

.twofa-card {
  padding: 8px 0 0;
}

.twofa-logo {
  filter: drop-shadow(0 10px 18px rgba(154, 84, 28, 0.18));
}

.twofa-copy {
  color: #4b2430;
}

.twofa-eyebrow {
  color: #9e212f;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  margin-bottom: 10px;
  text-transform: uppercase;
}

.twofa-title {
  color: #6e1623;
}

.twofa-description,
.twofa-email,
.twofa-help {
  color: #6b5560;
  line-height: 1.6;
  margin-bottom: 0.4rem;
}

.twofa-email strong {
  color: #4b2430;
}

.twofa-dev-code {
  display: inline-block;
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(158, 33, 47, 0.08);
  color: #8a1728;
  font-size: 0.92rem;
}

.otp-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.otp-input input {
  width: 50px;
  height: 50px;
  background: #fffaf7;
  color: #581b26;
  text-align: center;
  font-size: 1.2rem;
  font-weight: bold;
  border: 2px solid rgba(161, 128, 86, 0.34);
  border-radius: 12px;
  transition: all 0.2s ease;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.otp-input input:focus {
  border-color: #b8893f;
  box-shadow: 0 0 0 4px rgba(184, 137, 63, 0.18);
  outline: none;
}

.twofa-link {
  appearance: none;
  background: transparent;
  border: 0;
  color: #9e212f;
  cursor: pointer;
  font-weight: 700;
  padding: 0;
}

.twofa-link:disabled {
  cursor: wait;
  opacity: 0.65;
}

.twofa-link:hover,
.twofa-link:focus {
  color: #7d1220;
  outline: none;
  text-decoration: underline;
}
</style>
